import React, { useCallback, useMemo } from 'react';
// SvgXml removed — previews are always webp now
import {
  View,
  TouchableOpacity,
  Alert,
  ScrollView,
  Platform,
  Animated,
  Text as RNText,
  useWindowDimensions,
} from 'react-native';
import { Text } from '@/components';
import { Image } from 'expo-image';
import * as Haptics from 'expo-haptics';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useUserProfile, useCollectibles } from '@/lib/queries/account';
import { useDogs } from '@/context/DogsContext';
import { useMyTrails, useMyTrailsStats, useSavedTrails, useSaveTrailBookmark, useRemoveTrailBookmark, useIsTrailSaved, flattenTrailPages } from '@/lib/queries/trails';
import { useMyGroups, useMyInvites, useAcceptInvite } from '@/lib/queries/groups';
import { Trail } from '@/types/trail';
import { useNotificationsQuery } from '@/lib/queries/notifications';
import { useDogSvg, useTrailHistory, flattenHistoryPages } from '@/lib/queries/misc';

import { Mail, LogOut, Dog, MapPin, Star, BarChart3, Bookmark, ArrowRight, Users, Plus } from 'lucide-react-native';
import { BellIcon, HamburgerIcon } from '@/components/NavIcons';
import theme from '@/constants/colors';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import styles from './profile.styles';
import { Typography } from '@/constants/typography';
import { useRouter } from 'expo-router';
import { formatDistance } from '@/utils/distance';
import { LinearGradient } from 'expo-linear-gradient';
import TrailMapPreview from '@/components/TrailMapPreview';
import TrailCard from '@/components/TrailCard';
import AwardsGrid, { collectibleEarned } from '@/components/AwardsGrid';
import LottieLoader from '@/components/LottieLoader';
// Use bundler require for local asset so Metro/Expo can resolve it reliably
const Icon = require('../../assets/images/icon.png');

type ProfileTab = 'created' | 'saved' | 'history' | 'awards';

// ponytail: milestone target hardcoded from the design mock ("12/15 walks");
// make it a per-award backend field when real award progress lands.
const WALK_MILESTONE_TARGET = 15;

export default function ProfileScreen() {
  const auth = useAuth();
  if (!auth) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingContainer}>
          <LottieLoader size={120} />
        </View>
      </View>
    );
  }
  const { user, signOut, session } = auth;
  const { data: userProfile } = useUserProfile(session?.accessToken);
  const isAccountLoading = !userProfile;
  const { collectibles } = useCollectibles(session?.accessToken);
  const { dogProfile, dogProfiles, isDogProfileLoading } = useDogs();
  const myTrailsQuery = useMyTrails(session?.accessToken, user?.id);
  const trails = useMemo(() => flattenTrailPages(myTrailsQuery.data), [myTrailsQuery.data]);
  const savedTrailsQuery = useSavedTrails(session?.accessToken);
  const savedTrails = useMemo(() => flattenTrailPages(savedTrailsQuery.data), [savedTrailsQuery.data]);
  const statsQuery = useMyTrailsStats(session?.accessToken);
  const saveTrailBookmarkMutation = useSaveTrailBookmark(session?.accessToken);
  const removeTrailBookmarkMutation = useRemoveTrailBookmark(session?.accessToken);
  const isTrailSaved = useIsTrailSaved(session?.accessToken);

  const myInvitesQuery = useMyInvites(session?.accessToken);
  // Family member avatars for the header stack (design: overlapping avatars)
  const familyGroupsQuery = useMyGroups(session?.accessToken, 'family');
  const familyMembers = (familyGroupsQuery.data?.[0]?.members ?? []).filter(
    (m) => m.userId !== user?.id && !!m.image,
  );
  const myInvites = (myInvitesQuery.data ?? []).filter((i) => !i.acceptedAt);
  const acceptInviteMutation = useAcceptInvite(session?.accessToken);

  const saveTrailBookmark = useCallback(async (trailId: string) => {
    await saveTrailBookmarkMutation.mutateAsync(trailId);
  }, [saveTrailBookmarkMutation]);

  const removeTrailBookmark = useCallback(async (trailId: string) => {
    await removeTrailBookmarkMutation.mutateAsync(trailId);
  }, [removeTrailBookmarkMutation]);

  const notificationsQuery = useNotificationsQuery(session?.accessToken);
  const unreadCount = notificationsQuery.data?.unreadCount ?? 0;
  const [isCardSwiping, setIsCardSwiping] = React.useState<Record<string, boolean>>({});
  // Dog carousel pager: active page index (dogs + add-dog card)
  const [activeDogIndex, setActiveDogIndex] = React.useState(0);
  // Screen-width pages: content container has 16px padding each side (styles.content)
  const DOG_PAGE_GAP = 12;
  const { width: windowWidth } = useWindowDimensions();
  const dogPageWidth = windowWidth - 32;
  const dogPageStride = dogPageWidth + DOG_PAGE_GAP;
  const { t } = useTranslation();
  const mainScrollRef = React.useRef<ScrollView | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [selectedTab, setSelectedTab] = React.useState<ProfileTab>('created');
  const [bookmarkAnimations, setBookmarkAnimations] = React.useState<Record<string, Animated.Value>>({});
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const totalWalks = statsQuery.data?.totalWalks ?? 0;
  const totalTime = statsQuery.data?.totalTime ?? 0;
  const totalDistance = statsQuery.data?.totalDistance ?? 0;

  const getDayStreak = () => {
    try {
      const dayMs = 24 * 60 * 60 * 1000;
      const daysSet = new Set<number>();
      trails.forEach(t => {
        if (!t.date) return;
        const d = new Date(t.date);
        d.setHours(0, 0, 0, 0);
        daysSet.add(d.getTime());
      });

      let streak = 0;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      let cursor = today.getTime();

      while (daysSet.has(cursor)) {
        streak++;
        cursor -= dayMs;
      }

      return streak;
    } catch (e) {
      return 0;
    }
  };

  const dayStreak = getDayStreak();

  // Dog SVG — cached via React Query
  const dogSvgQuery = useDogSvg();
  const dogSvg = dogSvgQuery.data ?? null;

  // Trail history — loaded on demand when the 'history' tab is selected
  const historyQuery = useTrailHistory(session?.accessToken, selectedTab === 'history');
  const history = useMemo(() => flattenHistoryPages(historyQuery.data), [historyQuery.data]);

  // Collectibles — provided by React Query hooks

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}.${Math.floor(minutes / 6)}h`;
    }
    return `${minutes}m`;
  };

  const handleTabPress = (tab: ProfileTab) => {
    if (true) {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    }
    setSelectedTab(tab);
    if (tab === 'history') {
      // React Query will handle fetching when enabled
    }
  };

  // fetchHistory is now handled by useTrailHistory hook

  const handleNavigateToTrail = (trailId: string) => {
    if (true) {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    }
    router.push(`/trail/${trailId}`);
  };

  const getBookmarkAnimation = (trailId: string) => {
    if (!bookmarkAnimations[trailId]) {
      const newAnim = new Animated.Value(1);
      setBookmarkAnimations(prev => ({ ...prev, [trailId]: newAnim }));
      return newAnim;
    }
    return bookmarkAnimations[trailId];
  };

  const handleBookmarkPress = async (trailId: string) => {
    if (true) {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    }

    const anim = getBookmarkAnimation(trailId);
    const isSaved = isTrailSaved(trailId);

    Animated.sequence([
      Animated.timing(anim, {
        toValue: 0.6,
        duration: 100,
        useNativeDriver: true,
      }),
      Animated.spring(anim, {
        toValue: 1,
        friction: 3,
        tension: 100,
        useNativeDriver: true,
      }),
    ]).start();

    try {
      if (isSaved) {
        await removeTrailBookmark(trailId);
      } else {
        await saveTrailBookmark(trailId);
        if (true) {
          Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
        }
      }
    } catch (error) {
      console.error('Failed to toggle bookmark:', error);
    }
  };

  const handleSignOut = async () => {
    if (true) {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    }
    Alert.alert(
      t('profile.signOut'),
      t('profile.signOutConfirm'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('profile.signOut'),
          style: 'destructive',
          onPress: async () => {
            if (true) {
              Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
            }
            setIsLoading(true);
            try {
              await signOut();
            } catch (error: any) {
              if (true) {
                Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
              }
              Alert.alert(t('common.error'), error.message || t('profile.failedToSignOut'));
            } finally {
              setIsLoading(false);
            }
          },
        },
      ]
    );
  };

  const handleEditDogProfile = (dogId?: string) => {
    if (true) {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    }
    router.push({ pathname: '/onboarding/dog-profile', params: { from: 'profile', dogId: dogId || dogProfile?.id } } as any);
  };

  const handleAddDogProfile = () => {
    if (true) {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    }
    router.push({ pathname: '/onboarding/dog-profile', params: { from: 'profile', newDog: 'true' } } as any);
  };

  const getDisplayedTrails = (): Trail[] => {
    if (selectedTab === 'created') return trails;
    if (selectedTab === 'saved') return savedTrails;
    if (selectedTab === 'history') return history;
    return [];
  };

  const displayedTrails = getDisplayedTrails();

  // Active tab's infinite query drives pagination (awards tab has none).
  const activeQuery = selectedTab === 'created' ? myTrailsQuery : selectedTab === 'saved' ? savedTrailsQuery : selectedTab === 'history' ? historyQuery : null;
  const isTabLoading = activeQuery?.isLoading ?? false;
  const hasNextPage = activeQuery?.hasNextPage ?? false;
  const isFetchingNextPage = activeQuery?.isFetchingNextPage ?? false;
  const fetchNextPage = activeQuery?.fetchNextPage ?? (() => {});

  React.useEffect(() => {
    if (displayedTrails && displayedTrails.length > 0) {
      try {
        // console.log('ProfileScreen.displayedTrails sample:', displayedTrails[0]);
      } catch (err) {
        // ignore
      }
    }
  }, [displayedTrails]);

  return (
    <View style={styles.container}>
      <ScrollView
        ref={mainScrollRef}
        scrollEnabled={!Object.values(isCardSwiping).some(Boolean)}
        style={styles.scrollContainer}
        contentContainerStyle={[styles.content, { paddingTop: insets.top + 16, paddingBottom: insets.bottom + 20 }]}
        scrollEventThrottle={16}
        onScroll={(e) => {
          const { layoutMeasurement, contentOffset, contentSize } = e.nativeEvent;
          const distanceFromEnd = contentSize.height - layoutMeasurement.height - contentOffset.y;
          if (distanceFromEnd < 400 && hasNextPage && !isFetchingNextPage) fetchNextPage();
        }}
      >
        <View style={styles.userSection}>
          <View style={styles.userHeader}>
            <TouchableOpacity style={styles.userAvatar} onPress={() => router.push('/settings/account')}>
              {(userProfile?.image || Icon) ? (
                <Image
                  source={userProfile?.image || Icon}
                  style={styles.userAvatarImage}
                  contentFit="cover"
                  cachePolicy="memory-disk"
                />
              ) : (
                <View style={styles.initialAvatar}>
                  <Text style={styles.initialAvatarText}>{(userProfile?.name || user?.email || 'A').charAt(0).toUpperCase()}</Text>
                </View>
              )}
            </TouchableOpacity>
            {/* Overlapping family avatars (design: 30px, offset up-left, dark ring) */}
            {familyMembers.slice(0, 3).map((m) => (
              <Image
                key={m.userId}
                source={{ uri: m.image! }}
                style={styles.familyAvatar}
                contentFit="cover"
                cachePolicy="memory-disk"
              />
            ))}
            <View style={styles.userInfo}>
              <Text style={styles.userName}>{userProfile?.name || user?.email?.split('@')[0] || t('profile.accountHolder')}</Text>
              <View style={styles.userEmailRow}>
                <Mail size={14} color={theme.textMuted} />
                <Text style={styles.userEmail} numberOfLines={1} ellipsizeMode="tail">{user?.email || t('profile.notAvailable')}</Text>
              </View>
            </View>
            <View style={styles.headerActions}>
              {unreadCount > 0 && (
                <TouchableOpacity
                  style={styles.bellButton}
                  onPress={() => router.push('/(tabs)/notifications')}
                  accessibilityLabel={t('notifications.title')}
                  accessibilityRole="button"
                  hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                >
                  <BellIcon size={22} color={theme.textMuted} />
                  <View style={styles.badge}>
                    <RNText style={styles.badgeText}>
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </RNText>
                  </View>
                </TouchableOpacity>
              )}
              {unreadCount === 0 && (
                <TouchableOpacity
                  style={styles.bellButton}
                  onPress={() => router.push('/(tabs)/notifications')}
                  accessibilityLabel={t('notifications.title')}
                  accessibilityRole="button"
                  hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                >
                  <BellIcon size={22} color={theme.textMuted} />
                </TouchableOpacity>
              )}
              <TouchableOpacity
                style={styles.settingsButton}
                onPress={() => router.push('/settings')}
                accessibilityLabel={t('settings.title')}
                accessibilityRole="button"
                hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
              >
                <HamburgerIcon size={24} color={theme.textMuted} />
              </TouchableOpacity>
            </View>
          </View>
        </View>

        {isDogProfileLoading ? (
          <View style={styles.dogCardContainer}>
            <View style={styles.loadingDogCard}>
              <LottieLoader size={120} />
            </View>
          </View>
        ) : dogProfiles.length > 0 ? (
          <View style={styles.dogSection}>
            <View style={styles.dogSectionHeader}>
              <Text style={styles.dogSectionTitle}>{t('profile.myDogs')}</Text>
              <TouchableOpacity style={styles.addDogOutlineButton} onPress={handleAddDogProfile} activeOpacity={0.85}>
                <Plus size={8} color={theme.textMuted} />
                <Text style={styles.addDogOutlineText}>{t('profile.addDogProfile')}</Text>
              </TouchableOpacity>
            </View>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.dogCarousel}
              snapToInterval={dogPageStride}
              snapToAlignment="start"
              decelerationRate="fast"
              onScroll={(e) => setActiveDogIndex(Math.round(e.nativeEvent.contentOffset.x / dogPageStride))}
              scrollEventThrottle={16}
            >
              {dogProfiles.map((dog, idx) => (
                <TouchableOpacity
                  key={dog.id}
                  style={[styles.dogCarouselCard, { width: dogPageWidth }]}
                  onPress={() => {
                    // family dogs are read-only — no edit affordance
                    if (dog.ownerName) {
                      if (true) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                      return;
                    }
                    handleEditDogProfile(dog.id);
                  }}
                  activeOpacity={0.9}
                >
                  {dog.image ? (
                    <Image source={dog.image} style={styles.dogCarouselPhoto} contentFit="cover" cachePolicy="memory-disk" />
                  ) : (
                    <View style={[styles.dogCarouselPhoto, styles.dogPhotoPlaceholder]}>
                      <Dog size={40} color={theme.accentPrimary} />
                    </View>
                  )}
                  <View style={{ flex: 1, marginLeft: 20 }}>
                    <Text style={styles.dogCarouselName}>{dog.name}</Text>
                    {dog.ownerName ? (
                      <Text style={styles.dogCarouselBreed} numberOfLines={1}>{t('profile.familyDog', { owner: dog.ownerName })}</Text>
                    ) : dog.nickname ? (
                      <Text style={styles.dogCarouselBreed}>{dog.nickname}</Text>
                    ) : dog.breed ? (
                      <Text style={styles.dogCarouselBreed} numberOfLines={1}>{dog.breed.length > 22 ? dog.breed.slice(0, 22) + '…' : dog.breed}</Text>
                    ) : null}
                  </View>
                </TouchableOpacity>
              ))}
            </ScrollView>

            {/* Pager dots: only when swiping is actually possible (2+ dogs) */}
            {dogProfiles.length > 1 && (
              <View style={styles.dogDots}>
                {dogProfiles.map((_, i) => (
                  <View key={i} style={[styles.dogDot, i === Math.min(activeDogIndex, dogProfiles.length - 1) && styles.dogDotActive]} />
                ))}
              </View>
            )}

            {/* Stats row below the carousel */}
            <View style={styles.statsSection}>
              <View style={styles.statsGrid}>
                <View style={styles.statBox}>
                  <Text style={styles.statValueLarge}>{dayStreak}</Text>
                  <Text style={styles.statLabelSmall}>{t('profile.streak')}</Text>
                </View>
                <View style={styles.statBox}>
                  <Text style={styles.statValueLarge}>{totalWalks}</Text>
                  <Text style={styles.statLabelSmall}>{t('profile.walks')}</Text>
                </View>
                <View style={styles.statBox}>
                  <Text style={styles.statValueLarge}>{formatTime(totalTime)}</Text>
                  <Text style={styles.statLabelSmall}>{t('profile.totalTime')}</Text>
                </View>
                <View style={styles.statBox}>
                  <Text style={styles.statValueLarge}>{formatDistance(totalDistance)}</Text>
                  <Text style={styles.statLabelSmall}>{t('profile.totalDist')}</Text>
                </View>
              </View>
            </View>
          </View>
        ) : (
          <View style={styles.dogCardContainer}>
            <View style={styles.noDogCard}>
              <Dog size={48} color={theme.textMuted} />
              <Text style={styles.noDogText}>{t('profile.noDogProfile')}</Text>
              <TouchableOpacity
                style={styles.addDogButton}
                onPress={handleAddDogProfile}
              >
                <Text style={styles.addDogButtonText}>{t('profile.addDogProfile')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

          {/* Awards tab replaces the old collectibles preview card */}

          {/* Groups & invites */}
          <View style={{ paddingHorizontal: 16, marginTop: 12, gap: 8 }}>
            {/* <TouchableOpacity style={[styles.collectiblesContainer, { flexDirection: 'row', alignItems: 'center', padding: 14, gap: 10 }]} onPress={() => router.push('/(tabs)/groups' as any)}>
              <Users size={22} color={theme.accentPrimary} />
              <Text style={{ ...Typography.body(theme.textPrimary), fontSize: 17, fontWeight: '600' }}>{t('groups.title')}</Text>
            </TouchableOpacity> */}

            {myInvites.length > 0 && (
              <View style={{ gap: 8 }}>
                <Text style={{ ...Typography.label(theme.textMuted), marginBottom: 0 }}>{t('groups.invitations')}</Text>
                {myInvites.map((inv) => (
                  <View key={inv.id} style={[styles.collectiblesContainer, { flexDirection: 'row', alignItems: 'center', padding: 12, gap: 10 }]}>
                    <View style={{ flex: 1 }}>
                      <Text style={{ ...Typography.body(theme.textPrimary), fontSize: 16, fontWeight: '600' }}>{inv.groupName || 'Pack'}</Text>
                      <Text style={{ ...Typography.caption(theme.textMuted), fontSize: 13 }}>{t(`groups.${inv.groupType || 'pack'}`)}</Text>
                    </View>
                    <TouchableOpacity
                      style={{ backgroundColor: theme.accentPrimary, paddingHorizontal: 16, paddingVertical: 8, borderRadius: 12 }}
                      disabled={acceptInviteMutation.isPending}
                      onPress={async () => {
                        try {
                          await acceptInviteMutation.mutateAsync(inv.code);
                          Alert.alert(t('groups.joined'), t('groups.joinedBody'));
                        } catch (e: any) {
                          if (/already|409|accepted/i.test(e?.message || '')) {
                            Alert.alert(t('groups.joined'), t('groups.alreadyMember'));
                          } else {
                            Alert.alert(t('common.error'), e?.message || t('groups.inviteFailed'));
                          }
                        }
                      }}
                    >
                      <Text style={{ color: theme.backgroundPrimary, fontWeight: '700', fontSize: 15 }}>{t('groups.accept')}</Text>
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
            )}
          </View>

        <View style={styles.tabsContainer}>
          {(['created', 'saved', 'history', 'awards'] as ProfileTab[]).map((tab) => (
            <TouchableOpacity
              key={tab}
              style={[styles.tab, selectedTab === tab && styles.tabActive]}
              onPress={() => handleTabPress(tab)}
            >
              <Text style={[styles.tabText, selectedTab === tab && styles.tabTextActive]}>{t(`profile.${tab}`)}</Text>
            </TouchableOpacity>
          ))}
        </View>

        <View style={styles.trailsSection}>
          {selectedTab === 'awards' ? (
            <View style={styles.awardsSection}>
              <View style={styles.milestoneCard}>
                {(() => {
                  // Next-milestone award art = first locked collectible (design shows it greyed at 40%)
                  const nextAward = (collectibles ?? []).find((c) => !collectibleEarned(c));
                  return nextAward?.preview_url ? (
                    <Image source={{ uri: nextAward.preview_url }} style={[styles.milestoneArt, styles.milestoneArtLocked]} contentFit="cover" cachePolicy="memory-disk" />
                  ) : null;
                })()}
                <View style={{ flex: 1 }}>
                  <Text style={styles.milestoneLabel}>{t('profile.nextMilestone')}</Text>
                  <Text style={styles.milestoneTitle}>{t('profile.walkingBuddies')}</Text>
                  <View style={styles.milestoneTrack}>
                    <View style={[styles.milestoneFill, { width: `${Math.min(100, Math.round((totalWalks / WALK_MILESTONE_TARGET) * 100))}%` }]} />
                  </View>
                  <View style={styles.milestoneFooter}>
                    <Text style={styles.milestoneCount}>{`${Math.min(totalWalks, WALK_MILESTONE_TARGET)}/${WALK_MILESTONE_TARGET} ${t('profile.walks').toLowerCase()}`}</Text>
                    <Text style={styles.milestonePct}>{`${Math.min(100, Math.round((totalWalks / WALK_MILESTONE_TARGET) * 100))}%`}</Text>
                  </View>
                </View>
              </View>
              <Text style={styles.collectionTitle}>{t('profile.yourCollection')}</Text>
              <AwardsGrid collectibles={collectibles ?? []} />
            </View>
          ) : isTabLoading ? (
            <View style={styles.loadingContainer}>
              <LottieLoader size={160} />
            </View>
          ) : displayedTrails.length > 0 ? (
            <View style={styles.verticalTrailsContainer}>
              {displayedTrails.map((trail) => {
                const isSaved = isTrailSaved(trail.id);
                return (
                  <TrailCard
                    key={trail.id}
                    trail={trail}
                    onPress={(id) => handleNavigateToTrail(id)}
                    onBookmarkPress={(id) => handleBookmarkPress(id)}
                    isSaved={isSaved}
                    onSwipeStateChange={(id, swiping) => setIsCardSwiping(prev => ({ ...prev, [id]: swiping }))}
                  />
                );
              })}
              {isFetchingNextPage && (
                <View style={{ paddingVertical: 24, alignItems: 'center' }}>
                  <LottieLoader size={40} compact />
                </View>
              )}
            </View>
          ) : (
            <View style={styles.emptyContainer}>
              <MapPin size={48} color={theme.textMuted} />
              <Text style={styles.emptyText}>
                {selectedTab === 'created'
                  ? t('profile.noTrailsCreated')
                    : selectedTab === 'saved'
                    ? t('profile.noTrailsSaved')
                    : t('profile.noHistory')}
              </Text>
            </View>
          )}
        </View>

      </ScrollView>
    </View>
  );
}
